/**
 * №16.2.1 — the FULL PROCESS VIEW (own route, `place/:entity/:id/process`).
 *
 * A two-column LENS CONTAINER: a header (back · title · Active pill · Drive|Plan
 * toggle top-right) over the chosen lens body. The container owns everything the
 * two lenses share — the selection (a clicked step/decision/segment drives the
 * one properties rail), the decision preview, the lens choice (persisted per user
 * in `localStorage["wf-lens"]` and mirrored to `?lens=`), the `#seg-N` deep-link
 * scroll, and the confirmation-free run verbs bound to this instance. Read/EXECUTE
 * only — no authoring anywhere (D-WF14): a maker changes the template in the
 * console; here you only run it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useSearchParams } from "react-router";
import {
  useAnchorWorkflows, useInstanceDetail, useWhatsNext, useAgentDirectory,
  useWorkflowAdvance, useWorkflowRecordOutcome, useWorkflowClearOutcome,
  useWorkflowSkipStep, useWorkflowSetAssignee, useWorkflowSetDue, useWorkflowSetWorkState,
  useWorkflowSetInstructions, useWorkflowBulkAssign, useWorkflowBulkSetDue,
  useWorkflowBulkMarkDone, useWorkflowBulkSkip,
} from "../../hooks/use-workflow";
import { useWorkspaceMembers } from "../../hooks/use-workspace-members";
import { useAuth } from "../../../lib/hooks/use-auth";
import { showToast } from "../../components/primitives";
import { WorkflowDriveLens } from "../../components/workflow/WorkflowDriveLens";
import { WorkflowPlanLens } from "../../components/workflow/WorkflowPlanLens";
import type { LensData, Selection, Preview, WorkflowActions } from "../../components/workflow/wf-fullview-shared";
import "../../components/workflow/workflow-fullview.css";

type Lens = "drive" | "plan";

export function WorkflowFullView() {
  const { entity, id } = useParams<{ entity: string; id: string }>();
  const anchorType = entity ?? "matter";

  const anchor = useAnchorWorkflows(id, anchorType);
  const active = anchor.data?.instances.find((i) => i.status === "active") ?? anchor.data?.instances[0];
  const instanceNo = active?.instanceNo;
  const detail = useInstanceDetail(instanceNo);
  const next = useWhatsNext(instanceNo);
  const agentNames = useAgentDirectory().data;
  const members = useWorkspaceMembers().data?.members;
  const { user } = useAuth();

  // ── shared selection + preview state ──
  const [selected, setSelected] = useState<Selection | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);

  // ── lens choice: ?lens overrides localStorage, default "drive" ──
  const [searchParams, setSearchParams] = useSearchParams();
  const lensParam = searchParams.get("lens");
  const [lens, setLens] = useState<Lens>(() => {
    if (lensParam === "drive" || lensParam === "plan") return lensParam;
    return localStorage.getItem("wf-lens") === "plan" ? "plan" : "drive";
  });
  function chooseLens(l: Lens) {
    setLens(l);
    try { localStorage.setItem("wf-lens", l); } catch { /* private mode — url still carries it */ }
    const nsp = new URLSearchParams(searchParams);
    nsp.set("lens", l);
    setSearchParams(nsp, { replace: true });
  }

  // ── verbs ──
  const advance = useWorkflowAdvance();
  const recordOutcome = useWorkflowRecordOutcome();
  const clearOutcome = useWorkflowClearOutcome();
  const skipStep = useWorkflowSkipStep();
  const setAssignee = useWorkflowSetAssignee();
  const setDue = useWorkflowSetDue();
  const setWorkState = useWorkflowSetWorkState();
  const setInstructions = useWorkflowSetInstructions();
  const bulkAssign = useWorkflowBulkAssign();
  const bulkSetDue = useWorkflowBulkSetDue();
  const bulkMarkDone = useWorkflowBulkMarkDone();
  const bulkSkip = useWorkflowBulkSkip();
  const busy =
    advance.isPending || recordOutcome.isPending || clearOutcome.isPending || skipStep.isPending ||
    setAssignee.isPending || setDue.isPending || setWorkState.isPending || setInstructions.isPending ||
    bulkAssign.isPending || bulkSetDue.isPending || bulkMarkDone.isPending || bulkSkip.isPending;

  const actions = useMemo<WorkflowActions>(() => {
    const inst = instanceNo;
    function fire<T>(m: { mutate: (v: T, o: { onError: (e: unknown) => void; onSuccess: () => void }) => void }, v: T, ok: string) {
      m.mutate(v, { onError: (e) => showToast((e as Error).message, "error"), onSuccess: () => showToast(ok, "success") });
    }
    return {
      complete: (n) => inst != null && fire(advance, { instance_no: inst, step_entry_no: n }, "Step completed"),
      recordOutcome: (seg, outcome) => inst != null && fire(recordOutcome, { instance_no: inst, segment_no: seg, outcome }, "Decision recorded"),
      clearOutcome: (seg) => inst != null && fire(clearOutcome, { instance_no: inst, segment_no: seg }, "Choice cleared"),
      skip: (n, reason) => inst != null && fire(skipStep, { instance_no: inst, step_entry_no: n, reason }, "Step skipped"),
      setAssignee: (n, kind, code) => inst != null && fire(setAssignee, { instance_no: inst, step_entry_no: n, assignee_kind: kind, assignee_code: code ?? undefined }, "Reassigned"),
      setDue: (n, date) => inst != null && fire(setDue, { instance_no: inst, step_entry_no: n, date }, date ? "Due date set" : "Due date cleared"),
      setWorkState: (n, state) => inst != null && fire(setWorkState, { instance_no: inst, step_entry_no: n, state }, "Work state updated"),
      setInstructions: (n, instructions) => inst != null && fire(setInstructions, { instance_no: inst, step_entry_no: n, instructions }, "Briefing saved"),
      bulkAssign: (nos, kind, code) => inst != null && fire(bulkAssign, { instance_no: inst, step_entry_nos: nos, assignee_kind: kind, assignee_code: code ?? undefined }, "Reassigned"),
      bulkSetDue: (nos, date) => inst != null && fire(bulkSetDue, { instance_no: inst, step_entry_nos: nos, date }, "Due dates set"),
      bulkMarkDone: (nos) => inst != null && fire(bulkMarkDone, { instance_no: inst, step_entry_nos: nos }, "Marked done"),
      bulkSkip: (nos, reason) => inst != null && fire(bulkSkip, { instance_no: inst, step_entry_nos: nos, reason }, "Skipped"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceNo]);

  // ── the EFFECTIVE selection, derived (no set-state-in-effect) ──
  // Priority: an explicit user click > a `#seg-N` deep-link > the DO NEXT target.
  // Once the user clicks anything, `selected` wins and stays put.
  const { hash } = useLocation();
  const segments = detail.data?.segments;
  const hashSelection = useMemo<Selection | null>(() => {
    const m = /^#seg-(\d+)$/.exec(hash);
    if (!m || !segments) return null;
    const no = Number(m[1]);
    const seg = segments.find((s) => s.segmentNo === no);
    if (!seg) return null;
    return seg.hasDecision && seg.decision ? { kind: "decision", segmentNo: no } : { kind: "segment", segmentNo: no };
  }, [hash, segments]);
  const naSelection = useMemo<Selection | null>(() => {
    const na = next.data?.nextAction;
    if (!na || na.kind === "none") return null;
    if (na.kind === "decision") return { kind: "decision", segmentNo: na.segmentNo };
    return { kind: "step", segmentNo: na.segmentNo, stepEntryNo: na.stepEntryNo };
  }, [next.data]);
  const effectiveSelected = selected ?? hashSelection ?? naSelection;

  // ── deep-link: scroll `#seg-N` into view once per hash (DOM side effect only) ──
  const appliedHash = useRef<string | null>(null);
  const found = detail.data?.found;
  useEffect(() => {
    if (!found || appliedHash.current === hash) return;
    const m = /^#seg-(\d+)$/.exec(hash);
    if (!m) return;
    appliedHash.current = hash;
    const no = Number(m[1]);
    requestAnimationFrame(() => document.getElementById(`seg-${no}`)?.scrollIntoView({ behavior: "smooth", block: "center" }));
  }, [hash, found]);

  const d = detail.data;
  const title = d?.description && !d.description.startsWith("Auto-started") ? d.description : prettyCode(d?.templateCode ?? "Process");

  const lensData: LensData | null = d && d.found && next.data && instanceNo != null ? {
    instanceNo,
    detail: d,
    next: next.data,
    directory: { members: members ?? [], agentNames: agentNames ?? {}, currentUserId: user?.id },
  } : null;

  return (
    <div className="wfv">
      <div className="wfv-head">
        {d && d.found ? <h1>{title}</h1> : null}
        {d && d.found ? <span className="statuschip"><span className="dot" style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />{d.done ? "Finished" : "Active"}</span> : null}
        <div className="wfv-lens">
          <button className={lens === "drive" ? "is-on" : ""} onClick={() => chooseLens("drive")}>Drive</button>
          <button className={lens === "plan" ? "is-on" : ""} onClick={() => chooseLens("plan")}>Plan</button>
        </div>
      </div>

      {anchor.isLoading || detail.isLoading || next.isLoading ? (
        <div className="card"><div className="skeleton"><div className="sk-line" style={{ width: "40%" }} /><div className="sk-line" style={{ width: "80%" }} /><div className="sk-line" style={{ width: "65%" }} /></div></div>
      ) : !lensData ? (
        <div className="card"><div className="empty-state"><h3>No process on this record</h3><p>Nothing is running here yet. A workflow starts by hand or on create.</p></div></div>
      ) : lens === "plan" ? (
        <WorkflowPlanLens data={lensData} actions={actions} busy={busy} selected={effectiveSelected} onSelect={setSelected} preview={preview} onPreview={setPreview} />
      ) : (
        <WorkflowDriveLens data={lensData} actions={actions} busy={busy} selected={effectiveSelected} onSelect={setSelected} preview={preview} onPreview={setPreview} />
      )}
    </div>
  );
}

function prettyCode(code: string): string {
  return code.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
